import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { QuerstionService } from '../questionlist/table-complete/question.service';

/**
 * Restores the moderator session (see QuerstionService.restoreSession) once per
 * app load and routes accordingly:
 * - not logged in -> /login (carrying along ?scoId=... if the attempted route
 *   was the pod's /watch/:scoId deep link, see LoginComponent)
 * - already logged in (manually or restored) trying to open /login -> redirected
 *   straight to /list (if a meeting is being watched) or /meetings otherwise
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {

  constructor(private service: QuerstionService, private router: Router) { }

  public async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    await this.service.restoreSession();
    const isLoggedIn = this.service.restoreStatus === 'authenticated';

    // state.url includes the query string (e.g. '/login?scoId=...'), so compare
    // just the path - otherwise a /login navigation carrying ?scoId=... (see
    // WatchMeetingComponent) would fall through to the "not logged in" branch
    // below, which only reads _route.paramMap (path params, not query params)
    // and would redirect to a bare /login, dropping the scoId.
    const isLoginRoute = state.url.split('?')[0] === '/login';

    if (isLoginRoute) {
      if (!isLoggedIn) {
        return true;
      }
      const watchedScoId = this.service.getWatchedScoId();
      return this.router.createUrlTree([watchedScoId ? 'list' : 'meetings']);
    }

    if (!isLoggedIn) {
      const scoId = _route.paramMap.get('scoId');
      return this.router.createUrlTree(['login'], scoId ? { queryParams: { scoId } } : undefined);
    }

    return true;
  }
}
